import { useParams } from 'react-router';
import { useTrainerClient } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate, formatNumber } from '../../lib/format';
import { clientFlags } from './flags';
import { ClientAchievements, ClientWeeklySummaries } from './ClientInsights';
import { ClientNutrition } from './ClientNutrition';
import {
  Accordion,
  AccordionItem,
  Avatar,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  MeasurementCard,
  SkeletonCard,
  StatTile,
  Tabs,
} from '../../ui';

/** Odjeljci kartona klijenta. Prazan je `Pregled`, jer je on adresa klijenta. */
type Sekcija = 'prehrana' | 'treninzi' | 'mjere' | undefined;

/**
 * Karton klijenta.
 *
 * Do 12.09.2026. je bio jedan beskonačan stupac: profil, brojke, bilješka,
 * tjedni sažeci, mjere, povijest treninga, prehrana s biranjem dana i na kraju
 * postignuća. Marijev svakodnevni posao — pročitati što je klijent jeo i
 * ostaviti palac ili komentar — bio je na dnu te stranice, iza svega ostalog.
 *
 * Sada su odjeljci tabovi, i to LINKOVI: URL je izvor istine za stanje
 * (ARHITEKTURA.md sekcija 1), pa se prehrana klijenta može spremiti u
 * favorite, otvoriti u novoj kartici i vratiti tipkom natrag.
 *
 * Iznad tabova ostaje samo ono što vrijedi za sve odjeljke: tko je klijent,
 * koji program vozi i što traži pažnju.
 */
export function TrainerClientDetail() {
  const { id = '', sekcija } = useParams<{ id: string; sekcija: Sekcija }>();
  const { t, locale } = useTranslation();
  const query = useTrainerClient(id);

  const tabovi = [
    { to: `/trener/klijenti/${id}`, label: t('trainer.tabOverview'), end: true },
    { to: `/trener/klijenti/${id}/prehrana`, label: t('nutrition.title') },
    { to: `/trener/klijenti/${id}/treninzi`, label: t('trainer.tabWorkouts') },
    { to: `/trener/klijenti/${id}/mjere`, label: t('progress.measurements') },
  ];

  return (
    <QueryBoundary query={query} skeleton={<SkeletonCard />}>
      {(c) => (
        <>
          <PageHeader eyebrow={t('trainer.clientProfile')} title={c.name} subtitle={c.email} />

          <div className="flex flex-col gap-5">
            {/* Identitet klijenta stoji iznad tabova — vrijedi za svaki odjeljak. */}
            <Card className="flex flex-wrap items-center gap-4">
              <Avatar name={c.name} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="text-[14.5px] text-ink">{c.programName ?? '—'}</p>
                <p className="text-[13px] text-ink-muted">
                  {t('trainer.lastActive')}:{' '}
                  {c.lastActiveAt ? formatDate(c.lastActiveAt, locale) : '—'}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {clientFlags(c, t).map((f) => (
                    <Badge key={f.label} tone={f.tone}>
                      {f.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </Card>

            <Tabs items={tabovi} />

            {sekcija === undefined && (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  <StatTile
                    label={t('trainer.consistency')}
                    value={`${c.consistency.done}/${c.consistency.planned}`}
                  />
                  <StatTile
                    label={t('trainer.flagMissed', { n: '' }).trim()}
                    value={c.missedWorkouts}
                  />
                  <StatTile label={t('onboarding.goal')} value={c.goal} />
                </div>

                {c.notes && (
                  <Card>
                    <CardHeader title={t('training.trainerNote')} />
                    <p className="text-[14.5px] leading-relaxed text-ink-muted">{c.notes}</p>
                  </Card>
                )}

                <ClientWeeklySummaries clientId={id} />
                <ClientAchievements clientId={id} />
              </>
            )}

            {sekcija === 'prehrana' && <ClientNutrition clientId={id} />}

            {sekcija === 'treninzi' && (
              <section>
                <h2 className="eyebrow mb-3">{t('training.history')}</h2>
                {c.recentWorkouts.length === 0 ? (
                  <EmptyState message={t('empty.noWorkoutsPlanned')} />
                ) : (
                  <Accordion>
                    {c.recentWorkouts.map((w, i) => (
                      <AccordionItem
                        key={w.id}
                        defaultOpen={i === 0}
                        title={w.title}
                        meta={
                          <Badge tone={w.completedAt ? 'success' : 'warning'}>
                            {w.completedAt
                              ? formatDate(w.completedAt, locale)
                              : t('trainer.flagMissed', { n: 1 })}
                          </Badge>
                        }
                      >
                        <p>
                          {t('training.totalVolume')}: {formatNumber(w.volumeKg, locale)} kg
                        </p>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </section>
            )}

            {sekcija === 'mjere' && (
              <section>
                <h2 className="eyebrow mb-3">{t('progress.measurements')}</h2>
                {c.latestMeasurement ? (
                  <MeasurementCard
                    dateLabel={formatDate(
                      `${c.latestMeasurement.measuredOn}T00:00:00.000Z`,
                      locale,
                    )}
                    note={c.latestMeasurement.note}
                    rows={[
                      { label: t('dash.weight'), value: c.latestMeasurement.weightKg, unit: 'kg' },
                      { label: t('progress.waist'), value: c.latestMeasurement.waistCm, unit: 'cm' },
                      { label: t('progress.hips'), value: c.latestMeasurement.hipsCm, unit: 'cm' },
                    ]}
                  />
                ) : (
                  <EmptyState message={t('empty.noMeasurements')} />
                )}
              </section>
            )}
          </div>
        </>
      )}
    </QueryBoundary>
  );
}
